import { SubInstruction } from '@/types/workflow';

/**
 * Strip HTML tags and decode common HTML entities
 */
function stripHtmlTags(html: string): string {
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#8220;/g, '\u201C')
    .replace(/&#8221;/g, '\u201D')
    .replace(/&#8216;/g, '\u2018')
    .replace(/&#8217;/g, '\u2019')
    .replace(/&ldquo;/g, '\u201C')
    .replace(/&rdquo;/g, '\u201D')
    .replace(/&lsquo;/g, '\u2018')
    .replace(/&rsquo;/g, '\u2019')
    .replace(/&laquo;/g, '\u00AB')
    .replace(/&raquo;/g, '\u00BB')
    .replace(/&#171;/g, '\u00AB')
    .replace(/&#187;/g, '\u00BB')
    .trim();
}

/**
 * Check if a text line is a markdown header (## or ###)
 * Returns the title if it's a header, null otherwise
 * Handles optional surrounding quotes (straight, curly, French guillemets)
 */
function parseHeaderLine(line: string): string | null {
  let trimmed = line.trim();
  
  const openingQuotes = ['"', "'", '\u201C', '\u2018', '\u00AB', '\u201E', '\u2039'];
  const closingQuotes = ['"', "'", '\u201D', '\u2019', '\u00BB', '\u201D', '\u203A'];
  const allQuotes = [...new Set([...openingQuotes, ...closingQuotes])];
  
  // Remove surrounding quotes if present
  for (let i = 0; i < openingQuotes.length; i++) {
    if (trimmed.startsWith(openingQuotes[i]) && trimmed.endsWith(closingQuotes[i])) {
      trimmed = trimmed.slice(1, -1).trim();
      break;
    }
  }
  
  // Remove leading quote if present
  for (const q of allQuotes) {
    if (trimmed.startsWith(q)) {
      trimmed = trimmed.slice(1).trim();
      break;
    }
  }
  
  // Match ## or ### followed by space(s) and title
  const match = trimmed.match(/^(#{2,3})[\s\u00A0]+(.+)$/);
  if (match) {
    let title = match[2].trim();
    for (const q of allQuotes) {
      if (title.endsWith(q)) {
        title = title.slice(0, -1).trim();
        break;
      }
    }
    return title;
  }
  
  // Handle ##Title format (no space, starts with a number like ##4.8.1)
  const matchNoSpace = trimmed.match(/^(#{2,3})(\d+\..+)$/);
  if (matchNoSpace) {
    let title = matchNoSpace[2].trim();
    for (const q of allQuotes) {
      if (title.endsWith(q)) {
        title = title.slice(0, -1).trim();
        break;
      }
    }
    return title;
  }
  
  return null;
}

/**
 * Find header elements in HTML content and their positions.
 * Detects both native HTML heading tags (<h2>-<h6>) and
 * markdown headers inside paragraphs (<p>## Title</p>).
 */
function findHtmlHeaders(html: string): { index: number; length: number; title: string }[] {
  const headers: { index: number; length: number; title: string }[] = [];
  const seen = new Set<number>();
  let match;

  // Match <h2>-<h6> tags
  const hTagRegex = /<h([2-6])[^>]*>([\s\S]*?)<\/h\1>/gi;
  while ((match = hTagRegex.exec(html)) !== null) {
    const title = stripHtmlTags(match[2]);
    if (title && !seen.has(match.index)) {
      seen.add(match.index);
      headers.push({ index: match.index, length: match[0].length, title });
    }
  }

  // Match <p> tags containing ## markdown headers
  const pRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  while ((match = pRegex.exec(html)) !== null) {
    if (seen.has(match.index)) continue;
    const innerText = stripHtmlTags(match[1]);
    const title = parseHeaderLine(innerText);
    if (title) {
      seen.add(match.index);
      headers.push({ index: match.index, length: match[0].length, title });
    }
  }

  headers.sort((a, b) => a.index - b.index);
  return headers;
}

/**
 * Parse instruction text and extract sub-instructions based on headers.
 *
 * For HTML content: detects headers directly in the markup and splits
 * at their boundaries, preserving all rich content (tables, lists, etc.).
 *
 * For plain text: splits on markdown ## headers line by line.
 */
export function parseInstructionToSubSteps(text: string): {
  introText: string;
  subInstructions: SubInstruction[];
  hasHeaders: boolean;
} {
  if (!text || typeof text !== 'string') {
    return { introText: '', subInstructions: [], hasHeaders: false };
  }

  const isHtml = /<[^>]+>/.test(text);

  if (isHtml) {
    const headers = findHtmlHeaders(text);

    if (headers.length === 0) {
      return { introText: text, subInstructions: [], hasHeaders: false };
    }

    const introText = text.slice(0, headers[0].index).trim();

    const subInstructions: SubInstruction[] = headers.map((header, i) => {
      const contentStart = header.index + header.length;
      const contentEnd = i < headers.length - 1 ? headers[i + 1].index : text.length;
      return {
        title: header.title,
        content: text.slice(contentStart, contentEnd).trim(),
      };
    });

    return { introText, subInstructions, hasHeaders: true };
  }

  // Plain text path
  const lines = text.split('\n');
  const headerPositions: { lineIndex: number; title: string }[] = [];

  for (let i = 0; i < lines.length; i++) {
    const title = parseHeaderLine(lines[i]);
    if (title) {
      headerPositions.push({ lineIndex: i, title });
    }
  }

  if (headerPositions.length === 0) {
    return { introText: text, subInstructions: [], hasHeaders: false };
  }

  const introText = lines.slice(0, headerPositions[0].lineIndex).join('\n').trim();

  const subInstructions: SubInstruction[] = headerPositions.map((header, i) => {
    const next = headerPositions[i + 1];
    const contentLines = lines.slice(header.lineIndex + 1, next ? next.lineIndex : lines.length);
    return {
      title: header.title,
      content: contentLines.join('\n').trim(),
    };
  });

  return { introText, subInstructions, hasHeaders: true };
}

/**
 * Merge intro text and sub-instructions back into a single text with markdown headers.
 * Detects if content is HTML and formats output accordingly.
 */
export function mergeSubStepsToText(
  introText: string,
  subInstructions: SubInstruction[]
): string {
  const isHtml = /<[^>]+>/.test(introText) || 
    subInstructions.some(sub => /<[^>]+>/.test(sub.content));

  if (isHtml) {
    const parts: string[] = [];

    if (introText && introText.trim()) {
      parts.push(introText.trim());
    }

    for (const sub of subInstructions) {
      parts.push(`<p>## ${sub.title}</p>`);
      if (sub.content && sub.content.trim()) {
        parts.push(sub.content.trim());
      }
    }

    return parts.join('');
  }

  const parts: string[] = [];

  if (introText && introText.trim()) {
    parts.push(introText.trim());
  }

  for (const sub of subInstructions) {
    parts.push(`## ${sub.title}`);
    if (sub.content && sub.content.trim()) {
      parts.push(sub.content.trim());
    }
  }

  return parts.join('\n\n');
}

/**
 * Check if an instruction has sub-instructions (multi-instruction mode)
 */
export function isMultiInstruction(instruction: { text: string; subInstructions?: SubInstruction[] } | undefined): boolean {
  return !!(instruction?.subInstructions && instruction.subInstructions.length > 0);
}
